import { defineStore } from 'pinia';
import { ref, computed, watch, nextTick } from 'vue';
import user from '@/api/user';
import { LoginStatus } from '@/common/const';
import UserCard from '@/model/userCard';
import { getEvoUserInfo, getWebUserInfo } from '@/common/login';
import { cardType, sizeMap } from '@/common/size';
import { useEventBus, isLogin as isLoginInCommon } from '@common/logic';
import { EVENTS } from '@/common/bus';

export const useLoginStore = defineStore('login-panel', () => {
  const loginStatus = ref(LoginStatus.UNKNOWN); // 登录状态
  const userModule = ref<Partial<UserCard>>({}); // 用户模块
  const loginInfo = ref<{ vuserid?: string; [key: string]: any }>({}); // 登录信息
  const isLogin = computed(() => loginStatus.value === LoginStatus.LOGIN); // 是否登录
  const isEvo = import.meta.env.EVO;
  const isFamilySub = computed(() => userModule.value.userIdentity === cardType.FAMILY_SUB); // 是否为家庭卡
  const cardSize = ref(sizeMap[cardType.NO_LOGIN]); // 卡片大小
  const cardStyle = ref({}); // 卡片样式
  const offset = ref(0); // mac端三角形偏移量
  const hasNewMsg = ref(false); // 是否有新消息
  const isRequest = ref(false); // 是否正在请求
  const isHover = ref(false); // 是否悬停
  const eventBus = useEventBus();
  const isFisrtLoading = ref(true); // 是否是第一次加载
  const isShowCloseButton = ref(false); // 是否显示关闭按钮

  const publicReport = ref({
    mod_id: 'mod_profile_new',
    pgid: 'page_home_channel',
  });

  const getUserModule = async () => {
    if (isRequest.value) {
      return;
    }
    isRequest.value = true;
    const userModuleData = await user.getPersonalCenterModule();
    if (userModuleData.userIdentity === cardType.FAMILY_SUB) {
      cardSize.value = sizeMap[cardType.FAMILY_SUB];
    } else {
      cardSize.value = sizeMap[cardType.COMMON];
    }
    userModule.value = userModuleData;
    publicReport.value = {
      ...publicReport.value,
      ...(userModuleData?.userbasicReportInfo || {}),
    };
    cardStyle.value = userModuleData.cardStyle;
    console.info('[nav-login-panel] userModule', userModuleData);
    isRequest.value = false;
    if (isHover.value) {
      getLimitWrite(userModule.value.vipCard?.elementData);
      getLimitWrite(userModule.value.sportCard?.elementData);
      getLimitWrite(userModule.value.otherCard?.elementData);
      isHover.value = false;
    }
    isFisrtLoading.value = false;
  };
  const getLimitWrite = (elementData: any) => {
    user.getLimitWrite({
      ...elementData,
      action: 1,
    });
  };
  const changeCardSize = async () => {
    if (isEvo) {
      nextTick(() => {
        console.info('[nav-login-panel] changeCardSize', cardSize.value);
        const size = cardSize.value;
        window.page?.userinfo?.setsize(size.width, size.height);
      });
    }
  };
  eventBus.$on(EVENTS.SHOW_PANEL, () => {
    isHover.value = true;
    updateLoginStatus(isLoginInCommon());
  });
  const updateLoginStatus = async (login: boolean) => {
    loginStatus.value = login ? LoginStatus.LOGIN : LoginStatus.NOT_LOGIN;
    console.info('[nav-login-panel] updateLoginStatus', login);
    if (!login) {
      refreshStore();
      cardSize.value = sizeMap[cardType.NO_LOGIN];
    } else {
      await getUserInfo();
      getUserModule();
      getNewMsgCount();
    }
  };

  const getNewMsgCount = async () => {
    if (loginInfo.value.vuserid) {
      const newMsgCount = await user.GetNewMsgCount(loginInfo.value.vuserid);
      hasNewMsg.value = newMsgCount;
    }
  };

  watch(() => cardSize.value, () => {
    changeCardSize();
  }, { immediate: true });

  const getUserInfo = async () => {
    if (isEvo) {
      const evoUserInfo = await getEvoUserInfo();
      // 拼接信息
      loginInfo.value = {
        userAvatar: evoUserInfo.avatar,
        userNick: evoUserInfo.nickname || '腾讯网友',
        type: evoUserInfo.loginType,
        openid: evoUserInfo.openid,
        vuserid: evoUserInfo.vuserid,
        vcuid: evoUserInfo.vcuid,
      };
      return;
    }
    const userInfo = await getWebUserInfo();
    loginInfo.value = userInfo.loginInfo;
  };

  const refreshStore = () => {
    loginStatus.value = LoginStatus.UNKNOWN;
    userModule.value = {};
    loginInfo.value = {};
    cardSize.value = sizeMap[cardType.NO_LOGIN];
    publicReport.value = {
      mod_id: 'mod_profile_new',
      pgid: 'page_home_channel',
    };
    hasNewMsg.value = false;
    cardStyle.value = {};
  };

  return {
    loginStatus,
    loginInfo,
    isLogin,
    userModule,
    isFamilySub,
    cardStyle,
    publicReport,
    offset,
    hasNewMsg,
    isHover,
    isFisrtLoading,
    isShowCloseButton,

    getNewMsgCount,
    updateLoginStatus,
    getUserModule,
    getUserInfo,
  };
});
